import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import psHttp from '@patsnap/common-ps-http';
import { I18nService } from '@analytics/shared/services/i18n.interface';
import { HIRO_EVENT_PROXY, ModelContextType, type IEventProxy, type InitialContext, type HostProps } from '@patsnap/hiro-event-proxy';
import { ParagraphHighlightService } from '@/services/paragraph-highlight-service.interface';
import { PatentBasicService } from '@/domains/patent/services/patent-basic-service';
import getParams from 'library-@/utils/get-params.js';
import { useRoute } from 'vue-router/composables';
import { watch } from 'vue';

export const HiroEntryService = Symbol('HiroEntryService');

export interface HiroEntryService {
    getWujieProps: () => HostProps;
}

@injectable()
export class HiroEntryServiceImpl implements HiroEntryService {
    constructor(
        @inject(I18nService)
        private readonly _i18n: I18nService,
        @inject(HIRO_EVENT_PROXY)
        private readonly _hiroEventProxy: IEventProxy,
        @inject(ParagraphHighlightService)
        private readonly _paragraphHighlightService: ParagraphHighlightService,
        @inject(PatentBasicService)
        private readonly _patentBasicService: PatentBasicService,
    ) {
        this._hiroEventProxy.onHiroLoaded(()=> {
            this._paragraphHighlightService.updateContextMenu();
        });
        this._initWatchRoute();
    }

    private _initWatchRoute() {
        const route = useRoute();
        watch(
            () => route.query.patentId,
            (newVal) => {
                this._hiroEventProxy.emitNewChat();
            },
        );
    }

    public getInitialContext = async (): Promise<InitialContext> => {
        const urlParams: Record<string, string> = getParams();
        const patentId = urlParams.patentId;
        const patent = await this._patentBasicService.getPatentBase(patentId);
        return Promise.resolve({
            modelContexts: [{
                type: ModelContextType.PN,
                value: patent.PATENT_ID,
                label: patent.PN,
            }],
        });
    };

    public getWujieProps = (): HostProps => {
        return {
            locale: this._i18n.locale,
            // @ts-ignore
            httpRequest: psHttp,
            eventProxy: this._hiroEventProxy,
            getInitialContext: this.getInitialContext,
        };
    };
}
